import { ApolloError, useMutation } from '@apollo/client';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  AddOn,
  addAddOn,
  removeAddOn,
  repriceAddOns,
  selectAddOns,
} from '../features/addOns/addOnsSlice';
import {
  clearStoredDiscounts,
  selectIdempotencyKey,
  selectPaymentPreference,
  selectPotentialFranchiseId,
  selectSlotPrice,
  selectStoredPromoCreditAmount,
  setBookingSuccess,
  setCreatedWorkOrder,
  setIdempotencyKey,
  setPayerRequest,
  setReferralCode,
  setVanId,
  withdrawPrepay,
} from '../features/booking/bookingSlice';
import {
  repriceServices,
  selectServiceTypes,
} from '../features/serviceTypes/serviceTypesSlice';
import { Vendor } from '../interfaces/Customer';
import { getDeviceId, identifyBooker, mp } from '../util/analytics';
import {
  aprTrack,
  bookingAttributionInput,
  bookingCurrency,
} from '../util/aperioTracking';
import {
  fieldsRefused,
  idempotencyKeyRefused,
  newBookingKey,
} from '../util/bookingKey';
import {
  eligibleSubtotalCents as calcEligibleSubtotalCents,
  PaymentPreference,
  slotAdjustmentCents,
} from '../util/bookingPricing';
import {
  BOOKING_PRICE_CHANGED,
  basketChanges,
  serverLinePrices,
} from '../util/bookingReprice';
import {
  ROUTE_BOOK_CONFIRMATION,
  ROUTE_BOOK_PAYMENT,
} from '../util/constants';
import {
  CREATE_WORK_ORDER,
  CREATE_WORK_ORDER_CONSUMER,
  CREATE_WORK_ORDER_CONSUMER_CORE,
  CREATE_WORK_ORDER_WITHOUT_KEY,
} from '../util/gql';
import { clearReferral } from '../util/referralLink';
import { requestPayerPayment } from '../util/payerPayment';
import { slotInstant } from '../util/slotTime';
import {
  extractSpecificGraphqlErrors,
  graphqlErrorResponse,
} from '../util/validationErrorHelper';

/** A booking request that got no answer at all; safe to retry. */
export const NETWORK_ERROR = 'NETWORK_ERROR';
/**
 * The server answered the booking with an error it did not explain (a
 * malformed request, a crash). The customer did nothing wrong and their
 * connection is fine, so they are told so, with the number to call.
 */
export const SERVER_ERROR = 'SERVER_ERROR';

/**
 * The discount code's id as the API's `Int` (the stores keep it as a
 * string). A string here made GraphQL refuse every booking with a code,
 * WELCOMEBACK included, with a 400 (2026-11-04).
 */
export function discountCodeIdForApi(
  id: string | number | null | undefined,
): number | null {
  if (id === null || id === undefined || id === '') return null;
  const n = Number(id);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** What the API's `Int` discountAmount takes: whole cents, or null. */
function centsForApi(cents: number | null | undefined): number | null {
  if (cents === null || cents === undefined) return null;
  const n = Math.round(Number(cents));
  return Number.isFinite(n) ? n : null;
}

/** The HTTP status of an error the server answered (0: no answer). */
function answeredStatus(error: unknown): number {
  const network = (error as ApolloError | null)?.networkError as
    { statusCode?: number } | null | undefined;
  return Number(network?.statusCode) || 0;
}

/**
 * The consumer skin's own fields (docs/consumer-skin-api.md section 2),
 * plus the unit / buzzer. (The server's `extrasRequested` is never sent:
 * the add-on tiles are gone, owner 2026-10-21.)
 */
export interface ConsumerBookingFields {
  skin: 'consumer';
  engineStatus?: 'KNOWN' | 'TBC';
  engineAckHigherPrice?: boolean;
  vehicleUnverifiedText?: string | null;
  vehicleUnverifiedKind?: 'TYPED' | 'DESCRIBED' | null;
  apSid?: string | null;
  unit?: string | null;
}

/** Fields added after today's CreateWorkOrder, by document. */
const CONSUMER_EXTRA_FIELDS = ['unit', 'expectedTotalCents'];
const CONSUMER_CORE_FIELDS = [
  'skin', 'engineStatus', 'engineAckHigherPrice', 'vehicleUnverifiedText',
  'vehicleUnverifiedKind', 'apSid',
];

/** What differs between the screens that book (fleet Schedule, consumer). */
export interface BookingSubmitInput {
  /** Order discount in cents (code + Groupon fees), or null. */
  discountCents: number | null;
  discountCodeId?: string | null;
  /** Sent only when the caller says an account is to be made with it. */
  password?: string;
  vendor?: Vendor;
  /** As the caller has it (Schedule: the van staff picked). */
  vanId: number | null | undefined;
  /** "Send the bill to your partner" is the chosen way to pay. */
  payerActive: boolean;
  payerEmail?: string;
  payerName?: string;
  /** Staff booking for a customer: no identify, no payment page. */
  isStaff: boolean;
  /**
   * The booker ticked "Email me service reminders and offers" (Aperio,
   * express consent). Sent only when true; the server records it on the
   * customer. Never sent to analytics.
   */
  marketingEmailOptIn?: boolean;
  /** "OK to store my insurance and registration for reminders." */
  documentConsent?: boolean;
  referralCode?: string | null;
  /** The consumer skin's fields; absent on today's flow. */
  consumer?: ConsumerBookingFields;
  /** The fee rows the server may add to the basket (BOOKING_PRICE_CHANGED). */
  feeCatalog: AddOn[];
  /**
   * Consumer skin: the server quote's booked lines (consumerQuote), sent as
   * the line items instead of the basket, so the booking prices exactly
   * what the review showed.
   */
  lineItems?: { id: number; value: number }[];
  /** Consumer skin: the quote's day discount (cents, signed). */
  expectedSlotPriceAdjustmentCents?: number;
  /** Consumer skin: the quote's total (createWorkOrder expectedTotalCents). */
  expectedTotalCents?: number;
  /** After the booking exists and the page was left (Schedule logs in). */
  onBooked?: (workOrder: { id?: number | null }) => void;
}

/**
 * The one booking submit (QA 2026-09-25 #1 idempotency, BOOKING_PRICE_
 * CHANGED / SLOT_PRICE_CHANGED repricing, prepay and payer hand-offs).
 * Today's Schedule step and the consumer skin's details step both book
 * through it: two sets of screens, one code path.
 */
export default function useBookingSubmit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [constraints, setConstraints] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [priceChangedMessage, setPriceChangedMessage] = useState('');
  const [availabilityRefreshToken, setAvailabilityRefreshToken] = useState(0);

  const {
    serviceAddress,
    dateTime,
    contact: { email, name, phoneNumber },
  } = useAppSelector((s) => s.serviceLocation);
  const promoCreditAmount = useAppSelector(selectStoredPromoCreditAmount);
  const paymentPreference = useAppSelector(selectPaymentPreference);
  const slotPrice = useAppSelector(selectSlotPrice);
  const selectedServices = useAppSelector(selectServiceTypes);
  const selectedAddOns = useAppSelector(selectAddOns);
  const potentialFranchiseId = useAppSelector(selectPotentialFranchiseId);
  const carId = useAppSelector((state) => state.car.id);
  const customerId = useAppSelector((state) => state.customer.id);
  const userVehicleId = useAppSelector((state) => state.car.userVehicleId);
  const vehicleCustomName = useAppSelector((state) => state.car.name);
  const vin = useAppSelector((state) => state.car.vin);
  const franchiseIdSelect = useAppSelector((state) => state.franchise.id);
  const storedIdempotencyKey = useAppSelector(selectIdempotencyKey);

  const [createWorkOrder, {
    loading: workOrderLoading,
  }] = useMutation(CREATE_WORK_ORDER);
  // The same booking without the idempotency key, for a server that does
  // not know the field yet (a deploy where this client lands first).
  const [createWorkOrderWithoutKey, {
    loading: workOrderWithoutKeyLoading,
  }] = useMutation(CREATE_WORK_ORDER_WITHOUT_KEY);
  const [createConsumer, {
    loading: consumerLoading,
  }] = useMutation(CREATE_WORK_ORDER_CONSUMER);
  const [createConsumerCore, {
    loading: consumerCoreLoading,
  }] = useMutation(CREATE_WORK_ORDER_CONSUMER_CORE);

  // One booking request at a time (QA 2026-09-25 #1: a double-click sent
  // two CreateWorkOrder mutations and made two work orders). The ref stops
  // the second click of a double-click before React re-renders; the state
  // disables the button until the request settles.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const eligibleSubtotalCents = calcEligibleSubtotalCents(
    [...selectedServices, ...selectedAddOns],
  );

  const reportBookingError = (error: unknown, feeCatalog: AddOn[]) => {
    const response = graphqlErrorResponse(error);
    if (response?.error === 'SLOT_PRICE_CHANGED') {
      // Airline-style: the price for this time moved between quote and
      // booking. Reload prices and ask the customer to confirm.
      setErrorCode('SLOT_PRICE_CHANGED');
      setPriceChangedMessage(t('schedule.priceChanged'));
      setAvailabilityRefreshToken((token) => token + 1);
      mp.track('Booking - Slot Price Changed', {
        previousAdjustmentCents: slotPrice?.cents ?? 0,
        newAdjustmentCents: response?.slotPriceAdjustmentCents,
        path: window.location.pathname,
      });
      return;
    }
    if (response?.error === BOOKING_PRICE_CHANGED) {
      // The server prices the booking from its own list and it came to more
      // than the basket showed: put its prices in the basket so the total
      // below is the real one, and ask the customer to confirm.
      // It also adds the fee lines the portal always adds, and books no fee
      // row the basket does not call for: the basket follows.
      const prices = serverLinePrices(response);
      const { remove, add } = basketChanges(
        prices,
        { services: selectedServices, addOns: selectedAddOns },
        feeCatalog,
      );
      remove.forEach((id) => dispatch(removeAddOn(id)));
      add.forEach((addOn) => dispatch(addAddOn(addOn)));
      dispatch(repriceServices(prices));
      dispatch(repriceAddOns(prices));
      setErrorCode(BOOKING_PRICE_CHANGED);
      setPriceChangedMessage(t('schedule.servicePriceChanged'));
      mp.track('Booking - Price Changed', {
        shownTotalCents: response?.shownTotalCents ?? null,
        totalCents: response?.totalCents ?? null,
        path: window.location.pathname,
      });
      return;
    }
    if (response?.error === 'DISCOUNT_CODE_INVALID') {
      // The server re-checks the code at booking and would not apply it:
      // take it off the summary (its box comes back) so the customer sees
      // the real total, and can book again or try another code.
      dispatch(clearStoredDiscounts());
    }
    if (response?.statusCode === 422 && Array.isArray(response.message)) {
      setConstraints(response.message as never[]);
    }
    if (response?.statusCode === 400 && typeof response?.error === 'string') {
      setErrorCode(response.error);
    }
    if (!response && !(error as ApolloError)?.graphQLErrors?.length) {
      if (answeredStatus(error) > 0) {
        // The server answered, with an error of its own (e.g. a 400 for a
        // malformed request): not the customer's connection.
        setErrorCode(SERVER_ERROR);
        setErrorMessage(t('schedule.serverError'));
        mp.track('Booking - Server Error', {
          status: answeredStatus(error),
          path: window.location.pathname,
        });
        return;
      }
      // The request never got an answer (offline, a timeout). Trying again
      // is safe: the idempotency key makes a repeat return the same order.
      setErrorCode(NETWORK_ERROR);
      setErrorMessage(t('schedule.networkError'));
      return;
    }
    setErrorMessage(extractSpecificGraphqlErrors(error as ApolloError));
  };

  /** Clears the last refusal (a new attempt, or the customer fixed it). */
  const clearError = () => {
    setConstraints([]);
    setErrorCode('');
    setErrorMessage('');
    setPriceChangedMessage('');
  };

  const submit = async (input: BookingSubmitInput): Promise<boolean> => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setSubmitting(true);
    clearError();
    // This booking's key: made once, reused by every retry of it.
    const idempotencyKey = storedIdempotencyKey || newBookingKey();
    if (!storedIdempotencyKey) dispatch(setIdempotencyKey(idempotencyKey));
    let booked = false;
    try {
      const expectedSlotPriceAdjustmentCents = input
        .expectedSlotPriceAdjustmentCents ?? (dateTime
        ? slotAdjustmentCents(dateTime, eligibleSubtotalCents)
        : 0);
      // Aperio: a pay-at-visit booking checks out here (a prepaid one on
      // the payment page). No-op for every other tenant.
      if (paymentPreference !== PaymentPreference.prepay) {
        aprTrack('begin_checkout', {
          // What the customer pays: after the promo code (WELCOMEBACK
          // and the like), never the pre-code figure.
          value: Math.max(0, Math.round(input.expectedTotalCents ?? (
            selectedServices.concat(selectedAddOns)
              .reduce((sum, service) => (
                sum + (Number(service.value) || 0) * 100
              ), 0)
            + expectedSlotPriceAdjustmentCents
          )) - (centsForApi(input.discountCents) ?? 0)) / 100,
          currency: bookingCurrency(serviceAddress?.countryCode),
        });
      }
      const variables = {
        // The slot's own instant (the service area's wall time), not the
        // wall time read in the browser's zone (util/slotTime).
        date: dateTime ? slotInstant(dateTime) : null,
        vehicleId: carId,
        ...(vehicleCustomName && vehicleCustomName.trim() !== ''
          && { vehicleCustomName }),
        ...(vin && vin.trim() !== '' && { vin }),
        customerId,
        userVehicleId,
        franchiseIdSelect,
        potentialFranchiseId,
        vanId: input.vanId,
        contactInput: {
          name,
          email,
          phoneNumber,
          ...(input.marketingEmailOptIn === true
            && { marketingEmailOptIn: true }),
          ...(input.documentConsent === true
            && { documentConsent: true }),
        },
        addressInput: serviceAddress,
        lineItems: input.lineItems
          ? input.lineItems.map((line) => ({
            id: line.id,
            quantity: 1,
            value: Math.round(line.value),
          }))
          : selectedServices.concat(selectedAddOns).map((service) => (
            {
              id: service.id,
              quantity: 1,
              value: Math.round(service.value * 100),
            })),
        ...(input.password !== undefined && { password: input.password }),
        ...(input.vendor && { vendorListInput: input.vendor }),
        discountCodeId: discountCodeIdForApi(input.discountCodeId),
        discountAmount: centsForApi(input.discountCents),
        promoCreditAmount: promoCreditAmount
          ? Math.round(promoCreditAmount * 100)
          : null,
        paymentPreference,
        expectedSlotPriceAdjustmentCents,
        ...(input.referralCode && !discountCodeIdForApi(input.discountCodeId)
          && { referralCode: input.referralCode }),
        // Where the booking came from (Aperio only; null elsewhere).
        attribution: bookingAttributionInput(getDeviceId()),
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const send = async (vars: typeof variables): Promise<any> => {
        let data;
        const plain = async () => {
          try {
            return (await createWorkOrder({
              variables: { ...vars, idempotencyKey },
            })).data;
          } catch (error) {
            if (!idempotencyKeyRefused(error)) throw error;
            return (await createWorkOrderWithoutKey({ variables: vars })).data;
          }
        };
        if (input.consumer) {
          // The consumer skin's document, stepping down while the server
          // does not know its fields yet; never a booking without the key.
          const { unit, ...core } = input.consumer;
          const consumerVariables = {
            ...vars,
            idempotencyKey,
            ...core,
          };
          try {
            ({ data } = await createConsumer({
              variables: {
                ...consumerVariables,
                unit: unit || null,
                expectedTotalCents: input.expectedTotalCents ?? null,
              },
            }));
          } catch (error) {
            if (!fieldsRefused(error, CONSUMER_EXTRA_FIELDS)) throw error;
            try {
              ({ data } = await createConsumerCore({
                variables: consumerVariables,
              }));
            } catch (coreError) {
              if (!fieldsRefused(coreError, CONSUMER_CORE_FIELDS)) {
                throw coreError;
              }
              data = await plain();
            }
          }
        } else {
          data = await plain();
        }
        return data;
      };
      // A server that does not know the attribution's consentState
      // (2026-10-24) or internal flag (2026-10-25) yet: book without them
      // rather than not at all.
      // Only the field the server names is left out (each at most once).
      const sendAttributed = async (vars: typeof variables): Promise<any> => {
        try {
          return await send(vars);
        } catch (error) {
          const refused = (['consentState', 'internal'] as const).filter(
            (key) => vars.attribution?.[key] !== undefined
              && fieldsRefused(error, [key]),
          );
          if (!vars.attribution || !refused.length) throw error;
          const attribution = { ...vars.attribution };
          refused.forEach((key) => { delete attribution[key]; });
          return sendAttributed({ ...vars, attribution });
        }
      };
      let data;
      try {
        data = await sendAttributed(variables);
      } catch (error) {
        // A server that does not know a tick yet: book without the ticks
        // rather than not at all.
        const ticks = (['marketingEmailOptIn', 'documentConsent'] as const)
          .filter((key) => input[key] === true);
        const refused = ticks.filter((key) => fieldsRefused(error, [key]));
        if (!refused.length) throw error;
        const contactInput = { ...variables.contactInput };
        refused.forEach((key) => { delete contactInput[key]; });
        data = await sendAttributed({ ...variables, contactInput });
      }
      if (!data?.createWorkOrder) {
        reportBookingError(null, input.feeCatalog);
        return false;
      }
      booked = true;
      aprTrack('appointment_confirmed', {}, {
        once: String(data.createWorkOrder.id ?? 'booked'),
      });
      // The server books an address nobody serves as pay-at-visit, whatever
      // was chosen: the confirmation page must say the same.
      if (
        data.createWorkOrder.paymentPreference
        && data.createWorkOrder.paymentPreference !== PaymentPreference.prepay
      ) {
        dispatch(withdrawPrepay());
      }
      dispatch(setVanId(data.createWorkOrder.vanId));
      dispatch(setCreatedWorkOrder({
        workOrderId: data.createWorkOrder.id ?? null,
        prepaymentToken: data.createWorkOrder.prepaymentToken ?? null,
        existingAccount: data.createWorkOrder.existingAccount ?? null,
      }));
      dispatch(setBookingSuccess(true));
      // Mixpanel, every tenant: this browser's events belong to the booker's
      // user id from now on (a guest included), with first_booking_at set
      // once. Never when staff book for a customer - the technician must
      // not become the customer.
      if (!input.isStaff) {
        // The profile gets the booking's city / province / postal3 (never
        // the street or the full postal code).
        identifyBooker(data.createWorkOrder.user?.id, undefined, new Date(), {
          city: serviceAddress?.city,
          province: serviceAddress?.provinceCode,
          postalCode: serviceAddress?.postalCode,
        });
      }
      if (input.referralCode) {
        // Used (or found not to apply) - either way this link is done.
        clearReferral();
        dispatch(setReferralCode(null));
      }
      // "Send the bill to your partner": the booking is made; now its pay
      // link goes to the payer (the booker is copied). Whatever happens to
      // that email, the booking stands; the confirmation page says which.
      const payerToken = input.payerActive
        && data.createWorkOrder.paymentPreference === PaymentPreference.prepay
        ? data.createWorkOrder.prepaymentToken
        : null;
      if (payerToken) {
        const sentTo = (input.payerEmail || '').trim();
        const payerName = input.payerName || '';
        const result = await requestPayerPayment(
          payerToken,
          sentTo,
          payerName,
        );
        dispatch(setPayerRequest(result.ok
          ? {
            status: 'SENT',
            email: sentTo,
            name: result.name ?? (payerName.trim() || null),
            changesLeft: result.changesLeft,
          }
          : {
            status: 'FAILED',
            email: sentTo,
            name: payerName.trim() || null,
            changesLeft: 0,
          }));
      }
      // Prepay customers pay right away; the booking is already saved so
      // abandoning the payment page simply leaves it as pay-later. A booking
      // whose bill went to someone else goes straight to its confirmation.
      const goToPayment = paymentPreference === PaymentPreference.prepay
        && !!data.createWorkOrder.prepaymentToken
        && !input.isStaff
        && !input.payerActive;
      navigate(goToPayment ? ROUTE_BOOK_PAYMENT : ROUTE_BOOK_CONFIRMATION);
      input.onBooked?.(data.createWorkOrder);
      return true;
    } catch (ex) {
      reportBookingError(ex, input.feeCatalog);
      return false;
    } finally {
      // After a booking the page is left; a failed one can be retried.
      if (!booked) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  return {
    submit,
    submitting,
    loading: workOrderLoading || workOrderWithoutKeyLoading
      || consumerLoading || consumerCoreLoading,
    constraints,
    setConstraints,
    errorMessage,
    setErrorMessage,
    errorCode,
    setErrorCode,
    priceChangedMessage,
    setPriceChangedMessage,
    availabilityRefreshToken,
    clearError,
    potentialFranchiseId,
  };
}
